import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import { adventureCardApi as cardApi } from '~/pages/CardSaga/AdventureCard/adventureCard.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchAdventureCardApi = createAsyncThunk(
    'cardSaga/adventureCard/fetchAdventureCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await cardApi.getAll();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const toggleAdventureCardPublishedStateApi = createAsyncThunk(
    'cardSaga/adventureCard/toggleAdventureCardPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);
            throw rejectWithValue([]); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const createAdventureCardApi = createAsyncThunk(
    'cardSaga/adventureCard/createAdventureCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await cardApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);
export const storeAdventureCardApi = createAsyncThunk(
    'cardSaga/adventureCard/storeAdventureCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            await cardApi.store(values);

            return alertSwal('success', 'Card generated successfully!');
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const showAdventureCardApi = createAsyncThunk(
    'cardSaga/actionCard/showAdventureCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await cardApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateAdventureCardApi = createAsyncThunk(
    'cardSaga/adventureCard/updateAdventureCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            await cardApi.update(id, values);
            alertSwal('success', 'Card updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const deleteAdventureCardItemApi = createAsyncThunk(
    'cardSaga/adventureCard/deleteAdventureCardItemApi',
    async (params, { dispatch }) => {
        const { navigate, id } = params;

        try {
            await cardApi.delete(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const adventureCardSlice = createSlice({
    name: 'cardSaga/adventureCard',
    initialState: {
        rows: [],
        emptyModel: {},
        editedModel: {},
        frontSides: [],
        backSides: [],
        allPictures: [],
        categories: [],
    },
    reducers: {
        clearEditedAdventureCard(state) {
            state.editedModel = {};
        },
        updateCategories(state, { payload: category }) {
            state.categories.forEach((item, i, arr) => {
                if (item.id == category.id) {
                    arr[i] = category;
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdventureCardApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchAdventureCardApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleAdventureCardPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleAdventureCardPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createAdventureCardApi.fulfilled, (state, { payload }) => {
                state.emptyModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.categories = payload.categories;
                state.allPictures = payload.allPictures;
            })
            .addCase(createAdventureCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(storeAdventureCardApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeAdventureCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showAdventureCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.categories = payload.categories;
                state.allPictures = payload.allPictures;
            })
            .addCase(showAdventureCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(updateAdventureCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make ActionUpdate re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
            })
            .addCase(updateAdventureCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deleteAdventureCardItemApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedAdventureCard, updateCategories } = adventureCardSlice.actions;
